import { isHttpError } from './http'

/** Machine-readable failures the widget reacts to, rather than just showing. */
export type WidgetApiErrorCode =
  | 'SLOT_UNAVAILABLE'
  | 'BOOKING_NOT_AWAITING_PAYMENT'
  | 'FEATURE_REQUIRED'
  | 'VEHICLE_NOT_FOUND'
  | 'PRICE_CHANGED'
  | 'MOT_CLASS_NOT_TESTED'
  | 'SERVICE_NOT_FOR_VEHICLE'
  | 'VEHICLE_LOOKUP_NEEDED'
  | 'VEHICLE_TYPE_NEEDED'

/** Conflicts that are about the booking, not the time: never "pick another time". */
const NOT_ABOUT_THE_TIME: WidgetApiErrorCode[] = [
  'PRICE_CHANGED',
  'MOT_CLASS_NOT_TESTED',
  'SERVICE_NOT_FOR_VEHICLE',
  'VEHICLE_TYPE_NEEDED',
]

/**
 * Reads the `code` off a failed widget request.
 *
 * Contract errors come back as `{ success: false, message, code }`, with `code`
 * at the top level of the `ApiResponse` envelope. A code nested in `data` is
 * also accepted, so a handler that wraps it differently still works.
 */
export function readApiErrorCode(error: unknown): string | null {
  if (!isHttpError(error)) return null

  const body = error.response?.data as
    | { code?: unknown; data?: { code?: unknown } | null }
    | string
    | null
    | undefined
  if (!body || typeof body !== 'object') return null

  const code = body.code ?? body.data?.code
  return typeof code === 'string' && code.trim() ? code.trim() : null
}

export function hasApiErrorCode(error: unknown, code: WidgetApiErrorCode): boolean {
  return readApiErrorCode(error) === code
}

/**
 * Whether a request failed because the time is no longer free.
 *
 * The backend answers SLOT_UNAVAILABLE, but any 409 counts too: a conflict
 * can come back without the code, and its server message may be written for
 * the garage's staff, never for the customer.
 */
export function isSlotConflict(error: unknown): boolean {
  if (hasApiErrorCode(error, 'SLOT_UNAVAILABLE')) return true
  const code = readApiErrorCode(error)
  if (code && (NOT_ABOUT_THE_TIME as string[]).includes(code)) return false
  return isHttpError(error) && error.response?.status === 409
}

/**
 * Whether the booking was refused because a price is no longer the one the
 * widget showed (ACA-96). Nothing was booked; the lookup is run again so the
 * customer sees the new prices before booking again.
 */
export function isPriceChanged(error: unknown): boolean {
  return hasApiErrorCode(error, 'PRICE_CHANGED')
}

/**
 * Whether a vehicle lookup failed because the registration is unknown, rather
 * than because something went wrong on the way.
 *
 * The backend answers 404 with VEHICLE_NOT_FOUND. A backend from before that
 * answered 400 with no code, so a bare 400 or 404 counts too.
 */
export function isVehicleNotFound(error: unknown): boolean {
  if (hasApiErrorCode(error, 'VEHICLE_NOT_FOUND')) return true
  if (!isHttpError(error)) return false
  const status = error.response?.status
  return status === 400 || status === 404
}

export const VEHICLE_TYPE_NEEDED_MESSAGE = 'Please tell us what type of vehicle this is.'

/**
 * The server's words for 409 VEHICLE_TYPE_NEEDED (ACA-110): an MOT whose class
 * is still unknown, with no answer to "What is it?". Null for any other error.
 */
export function vehicleTypeNeededMessage(error: unknown): string | null {
  if (!hasApiErrorCode(error, 'VEHICLE_TYPE_NEEDED') || !isHttpError(error)) return null
  const message = (error.response?.data as { message?: unknown } | null)?.message
  return typeof message === 'string' && message.trim() ? message.trim() : VEHICLE_TYPE_NEEDED_MESSAGE
}
